/**
 * Staged uploads for the Case IDE upload modal.
 *
 * The modal is a confirmation step: files are hashed and uploaded to a
 * per-session staging prefix (`fs-staging/{ind}/{session}/…`) while the user
 * is still looking at them, the API is asked what each one would collide
 * with, and nothing reaches the case tree until `commitStagedUpload` moves the
 * staged blobs in through `/fs/import`. Cancel deletes the session; a bucket
 * lifecycle rule collects whatever a closed tab left behind.
 *
 * The browser never has bucket permissions: for each file the API mints a
 * GCS resumable-upload session scoped to that one object (and to this
 * origin), and the bytes are PUT to it directly, so neither Storage security
 * rules nor the API's request-size cap are involved.
 */
import axios from "axios";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { filterDocumentsBySupport } from "../../../helpers/file_helpers";
import { mapWithConcurrency } from "../../../lib/mapWithConcurrency";
import {
  FilesystemAPI,
  type ConflictPolicy,
  type ImportManifestEntry,
  type StagingCheckResult
} from "../api/filesystemApi";
import type { FsNode } from "../types";
import { withoutSystemFiles, type CollectedFile } from "./collectDroppedFiles";

export type StagedStatus =
  /** Being hashed / uploaded / checked; not yet addable. */
  "staging" | "ready" | "failed" | "unsupported";

export type StagedFile = {
  id: string;
  file: File;
  /** Path relative to the drop root, e.g. `"Awards/2024/prize.pdf"`. */
  relativePath: string;
  name: string;
  sizeBytes: number;
  /** Order of arrival, for the rail's default sort. */
  addedAt: number;
  status: StagedStatus;
  /** Upload progress 0–100 while `staging`. */
  progress: number;
  sha256: string | null;
  /** Where the bytes sit in staging once uploaded. */
  storagePath: string | null;
  error: string | null;
  /** What the case already has that this file matches; null until checked. */
  check: StagingCheckResult | null;
};

let seq = 0;
const nextId = () => {
  seq += 1;
  return `staged-${seq}`;
};

const newSessionId = () =>
  typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID().replace(/-/g, "")
    : `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`;

/** How many files are hashed and uploaded at once for one batch. */
const UPLOAD_CONCURRENCY = 4;

const toHex = (buffer: ArrayBuffer) =>
  Array.from(new Uint8Array(buffer))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");

/** sha256 of a file's bytes, hex-encoded (WebCrypto). */
export const sha256Hex = async (file: Blob): Promise<string> => {
  const bytes = await file.arrayBuffer();
  const digest = await crypto.subtle.digest("SHA-256", bytes);
  return toHex(digest);
};

const contentTypeOf = (file: File) => file.type || "application/octet-stream";

/**
 * Ask the API for an upload session for this file, then PUT the bytes to it.
 * Resolves to the staging `storage_path` the API chose. The PUT goes through
 * bare `axios`, not `axiosInstance`: the Firebase bearer token must not be
 * sent to GCS, and the session URI is the only credential it needs.
 */
const uploadWithProgress = async (
  individualId: string,
  sessionId: string,
  entry: { file: File; relativePath: string },
  onProgress: (fraction: number) => void
): Promise<string> => {
  const contentType = contentTypeOf(entry.file);
  const session = await FilesystemAPI.createStagingUpload(
    individualId,
    sessionId,
    {
      relative_path: entry.relativePath,
      content_type: contentType,
      size_bytes: entry.file.size
    }
  );
  await axios.put(session.upload_url, entry.file, {
    headers: { "Content-Type": contentType },
    onUploadProgress: (event) => {
      const total = event.total ?? entry.file.size;
      onProgress(total ? event.loaded / total : 0);
    }
  });
  return session.storage_path;
};

export type StagedUpload = {
  sessionId: string;
  files: StagedFile[];
  /** Hash, upload and check a dropped or picked batch. */
  add: (collected: CollectedFile[]) => Promise<void>;
  remove: (id: string) => void;
  retry: (id: string) => Promise<void>;
  /** Delete the staging session (best effort) and forget the files. */
  cancel: () => Promise<void>;
  /** Forget the files without touching storage (after a commit). */
  reset: () => void;
  /** True while any file is still hashing, uploading or being checked. */
  isStaging: boolean;
};

/**
 * Hashes, uploads and checks files for one open upload modal. `parentId` is
 * the folder the files would land in; it scopes the same-name conflict check
 * (duplicate detection is case-wide regardless).
 */
export const useStagedUpload = (
  individualId: string,
  parentId: string | null
): StagedUpload => {
  const [sessionId, setSessionId] = useState(newSessionId);
  const [files, setFiles] = useState<StagedFile[]>([]);
  const filesRef = useRef(files);
  useEffect(() => {
    filesRef.current = files;
  }, [files]);

  const patch = useCallback((id: string, updates: Partial<StagedFile>) => {
    setFiles((current) =>
      current.map((f) => (f.id === id ? { ...f, ...updates } : f))
    );
  }, []);

  const stageOne = useCallback(
    async (entry: StagedFile) => {
      try {
        const [sha256, storagePath] = await Promise.all([
          entry.sha256 ?? sha256Hex(entry.file),
          entry.storagePath ??
            uploadWithProgress(individualId, sessionId, entry, (fraction) =>
              patch(entry.id, { progress: Math.round(fraction * 100) })
            )
        ]);
        patch(entry.id, { sha256, storagePath, progress: 100 });
        return { id: entry.id, sha256, storagePath };
      } catch (error) {
        patch(entry.id, {
          status: "failed",
          error:
            error instanceof Error && error.message
              ? error.message
              : "Upload failed"
        });
        return null;
      }
    },
    [individualId, patch, sessionId]
  );

  const checkBatch = useCallback(
    async (staged: { id: string; sha256: string }[]) => {
      if (!staged.length) return;
      const byId = new Map(filesRef.current.map((f) => [f.id, f]));
      const items = staged.map(({ id, sha256 }) => {
        const f = byId.get(id);
        return {
          sha256,
          name: f?.name ?? "",
          size_bytes: f?.sizeBytes ?? 0,
          parent_id: parentId
        };
      });
      try {
        const { results } = await FilesystemAPI.checkStaging(
          individualId,
          items
        );
        setFiles((current) =>
          current.map((f) => {
            const at = staged.findIndex((s) => s.id === f.id);
            if (at < 0) return f;
            const result = results[at] ?? null;
            return {
              ...f,
              check: result,
              status: f.status === "failed" ? "failed" : "ready"
            };
          })
        );
      } catch {
        // The check is advisory: without it the files are still addable, they
        // just carry no duplicate hints.
        setFiles((current) =>
          current.map((f) =>
            staged.some((s) => s.id === f.id) && f.status !== "failed"
              ? { ...f, status: "ready" }
              : f
          )
        );
      }
    },
    [individualId, parentId]
  );

  const add = useCallback(
    async (collected: CollectedFile[]) => {
      const candidates = withoutSystemFiles(collected).filter(
        (c) => c.file.size > 0
      );
      if (!candidates.length) return;
      const supported = new Set(
        filterDocumentsBySupport(
          candidates.map((c) => c.file),
          true
        )
      );
      const now = Date.now();
      const batch: StagedFile[] = candidates.map((c, i) => ({
        id: nextId(),
        file: c.file,
        relativePath: c.relativePath,
        name: c.file.name,
        sizeBytes: c.file.size,
        addedAt: now + i,
        status: supported.has(c.file) ? "staging" : "unsupported",
        progress: 0,
        sha256: null,
        storagePath: null,
        error: null,
        check: null
      }));
      setFiles((current) => [...current, ...batch]);
      const staged = await mapWithConcurrency(
        batch.filter((f) => f.status === "staging"),
        UPLOAD_CONCURRENCY,
        stageOne
      );
      await checkBatch(
        staged.filter((s): s is NonNullable<typeof s> => s !== null)
      );
    },
    [checkBatch, stageOne]
  );

  const retry = useCallback(
    async (id: string) => {
      const entry = filesRef.current.find((f) => f.id === id);
      if (!entry || entry.status !== "failed") return;
      patch(id, { status: "staging", error: null, progress: 0 });
      const staged = await stageOne({ ...entry, status: "staging" });
      if (staged) await checkBatch([staged]);
    },
    [checkBatch, patch, stageOne]
  );

  const remove = useCallback((id: string) => {
    setFiles((current) => current.filter((f) => f.id !== id));
  }, []);

  const reset = useCallback(() => {
    setFiles([]);
    setSessionId(newSessionId());
  }, []);

  const cancel = useCallback(async () => {
    const hadBlobs = filesRef.current.some((f) => f.storagePath);
    reset();
    if (hadBlobs) {
      try {
        await FilesystemAPI.deleteStagingSession(individualId, sessionId);
      } catch {
        // Best effort; the bucket lifecycle rule is the guarantee.
      }
    }
  }, [individualId, reset, sessionId]);

  const isStaging = useMemo(
    () => files.some((f) => f.status === "staging"),
    [files]
  );

  return { sessionId, files, add, remove, retry, cancel, reset, isStaging };
};

export type CommitDecision = {
  id: string;
  onConflict: ConflictPolicy;
};

/**
 * Move the chosen staged files into the case. `decisions` carries the
 * conflict policy for files whose name is already taken in the folder; files
 * without one keep both. Files that are not `ready` are ignored.
 */
/**
 * Files per `/fs/import` request. A large drop goes in several short requests
 * so the modal can count them in and one slow request never holds the lot.
 */
export const COMMIT_CHUNK = 25;

/** Some chunks landed before one failed: those files are in the case. */
export class PartialCommitError extends Error {
  constructor(
    message: string,
    readonly added: FsNode[],
    /** Staged file ids already in the case, to drop from the modal. */
    readonly committedIds: string[]
  ) {
    super(message);
    this.name = "PartialCommitError";
  }
}

export const commitStagedUpload = async (
  individualId: string,
  parentId: string | null,
  files: StagedFile[],
  decisions: CommitDecision[] = [],
  onProgress?: (done: number, total: number) => void
): Promise<FsNode[]> => {
  const policy = new Map(decisions.map((d) => [d.id, d.onConflict]));
  const ready = files.filter((f) => f.status === "ready" && f.storagePath);
  const entryOf = (f: StagedFile): ImportManifestEntry => ({
    relative_path: f.relativePath,
    storage_path: f.storagePath as string,
    mime_type: f.file.type || undefined,
    size_bytes: f.sizeBytes,
    checksum: f.sha256 ?? undefined,
    on_conflict: policy.get(f.id) ?? "keep_both"
  });
  if (!ready.length) return [];
  const added: FsNode[] = [];
  const committedIds: string[] = [];
  onProgress?.(0, ready.length);
  for (let at = 0; at < ready.length; at += COMMIT_CHUNK) {
    const chunk = ready.slice(at, at + COMMIT_CHUNK);
    try {
      // eslint-disable-next-line no-await-in-loop -- one chunk at a time keeps names and order deterministic
      const response = await FilesystemAPI.importManifest(
        individualId,
        chunk.map(entryOf),
        parentId
      );
      added.push(...response.nodes);
      committedIds.push(...chunk.map((f) => f.id));
      onProgress?.(committedIds.length, ready.length);
    } catch (error) {
      if (!added.length) throw error;
      throw new PartialCommitError(
        `Added ${committedIds.length} of ${ready.length} files; the rest could not be added.`,
        added,
        committedIds
      );
    }
  }
  return added;
};

export type DirectUploadResult = {
  added: FsNode[];
  /** Names of files that did not reach the case. */
  failed: string[];
};

/** The files `uploadFilesToCase` attempts: no system files, empty files or unsupported types. */
const uploadableFiles = (files: File[]): File[] => {
  const candidates = withoutSystemFiles(
    files.map((file) => ({ file, relativePath: file.name }))
  )
    .map((c) => c.file)
    .filter((file) => file.size > 0);
  const supported = new Set(filterDocumentsBySupport(candidates, true));
  return candidates.filter((file) => supported.has(file));
};

/**
 * Upload files straight into a case's root, without the review modal (the
 * new case wizard's notice). Same staging and `/fs/import` path as the
 * modal; same-name files keep both.
 */
export const uploadFilesToCase = async (
  individualId: string,
  files: File[]
): Promise<DirectUploadResult> => {
  const sessionId = newSessionId();
  const now = Date.now();
  const staged = await mapWithConcurrency(
    uploadableFiles(files).map((file) => ({ file, relativePath: file.name })),
    UPLOAD_CONCURRENCY,
    async (c): Promise<StagedFile | null> => {
      try {
        const [sha256, storagePath] = await Promise.all([
          sha256Hex(c.file),
          uploadWithProgress(individualId, sessionId, c, () => undefined)
        ]);
        return {
          id: nextId(),
          file: c.file,
          relativePath: c.relativePath,
          name: c.file.name,
          sizeBytes: c.file.size,
          addedAt: now,
          status: "ready",
          progress: 100,
          sha256,
          storagePath,
          error: null,
          check: null
        };
      } catch {
        return null;
      }
    }
  );
  const ready = staged.filter((f): f is StagedFile => f !== null);
  const readyFiles = new Set(ready.map((f) => f.file));
  let added: FsNode[] = [];
  let committed = ready;
  try {
    added = await commitStagedUpload(individualId, null, ready);
  } catch (error) {
    if (!(error instanceof PartialCommitError)) {
      committed = [];
    } else {
      added = error.added;
      committed = ready.filter((f) => error.committedIds.includes(f.id));
    }
  }
  const landed = new Set(committed.map((f) => f.file));
  const failed = files
    .filter((f) => f.size > 0 && (!readyFiles.has(f) || !landed.has(f)))
    .map((f) => f.name);
  return { added, failed };
};
